import React, { FC, useMemo, useCallback, useState, useEffect } from 'react';
import { DifferentiationsContainer } from '../../Differentiation';
import { NewAssignmentForm } from '../../../../assignment/components';
import { useTranslation } from 'react-i18next';
import {
  ITrajectoryDetail,
  TrajectorySectionCode,
  TrajectoryViewMode,
} from '../../../../../config/api/models/Trajectory';
import { TabbedContent, TabbedContentData } from '../../../../shared/components';
import { setSidebarTabsVisible } from 'src/modules/shared/slices/trajectorySidebar/entity/trajectorySidebarSlice';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { useUser } from 'src/modules/shared';
import { isSchoolHours } from 'src/modules/shared/helpers/isSchoolHours';
import DemoDialog, { ModalProps } from 'src/modules/app/components/Demo/DemoDialog';
import { setHasSeemAssignments } from 'src/modules/shared/slices/ctas/CtasSlice';
import { LogActionTypes } from 'src/modules/shared/data-logging';
import PageManagementSidebarContentNew from './page-management/PageManagementSidebarContentNew';
import { showHelpModals } from 'src/modules/shared/constants';

type TeacherSidebarContentProps = {
  showTabs: boolean;
  trajectory: ITrajectoryDetail;
  pageId?: string;
  mode: TrajectoryViewMode;
  sectionCode: TrajectorySectionCode;
};

const env = process.env.REACT_APP_ENVIRONMENT;

export const TeacherSidebarContent: FC<TeacherSidebarContentProps> = ({
  showTabs,
  trajectory,
  mode,
  sectionCode,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const currentBookId = useAppSelector((state) => state.trajectories.currentBookId);
  const currentChapterId = useAppSelector((state) => state.trajectories.currentChapterId);
  const hasSeemAssignments = useAppSelector((state) => state.ctasSlice.hasSeemAssignments);
  const { isTeacher } = useUser();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const assignmentsModalProps: ModalProps = {
    title: 'Zet de lesmethode naar jouw hand',
    hasSeen: () => dispatch(setHasSeemAssignments(true)),
    body: [
      'Leerstof weglaten of toevoegen? Dat kan!',
      'Eenvoudigere of moeilijkere versies maken? Geen probleem.',
      <>
        Zelfs <strong>eigen presentaties of Bookwidgets</strong> sluit je met gemak in.
      </>,
    ],
    videoUrl: 'https://vimeo.com/912504127?share=copy',
    checkboxLabel: 'Verberg deze melding',
    buttonText: 'SLUIT',
    log: LogActionTypes.ASSIGNMENTS_SEEN_CTA,
  };

  const handleSiderbarChange = useCallback((selectedTab: number) => {
    return setSelectedTab(selectedTab);
  }, []);

  useEffect(() => {
    setShowDialog(
      !hasSeemAssignments &&
        (env === 'demo' || (env !== 'demo' && !isSchoolHours() && isTeacher)) &&
        selectedTab === 1 &&
        showHelpModals
    );
  }, [hasSeemAssignments, isTeacher, selectedTab]);

  const teacherSidebarContent = useMemo((): TabbedContentData[] => {
    const onChangeTabsVisibility = (value: boolean) => dispatch(setSidebarTabsVisible(value));

    const content = [
      {
        title: t('trajectory.sidebar.differentiations.title'),
        data: (
          <DifferentiationsContainer
            bookId={currentBookId! || ''}
            trajectory={trajectory}
            chapterId={currentChapterId! || ''}
            onChangeScreen={(value: boolean) => onChangeTabsVisibility(value)}
          />
        ),
      },
      {
        title: t('trajectory.sidebar.assignments.title'),
        data: <NewAssignmentForm bookId={currentBookId!} trajectory={trajectory} />,
      },
      /*       {
        title: t('trajectory.sidebar.curriculumGoals.title'),
        data: <CurriculumGoalsSidebarContent />,
      }, */
    ];

    if (mode === 'edit' && ['0', '1'].includes(sectionCode)) {
      content.splice(0, 0, {
        title: t('trajectory.sidebar.pageManagement.title'),
        data: <PageManagementSidebarContentNew bookId={currentBookId!} />,
      });
    }

    return content;
  }, [trajectory, currentBookId, currentChapterId, mode, sectionCode, t, dispatch]);

  return (
    <>
      <TabbedContent
        onChange={(selectedTab) => handleSiderbarChange(selectedTab)}
        ariaPrefix="trajectory-sidebar"
        hideTabs={!showTabs}
        content={teacherSidebarContent}
      />
      {isTeacher && (
        <DemoDialog
          showDialog={showDialog}
          onClose={() => setShowDialog(false)}
          props={assignmentsModalProps}
        />
      )}
    </>
  );
};
